import React, { useState, useEffect } from 'react';
import './css/LabContent.css';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRightLong, faChevronDown, faChevronUp, faPersonRunning, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import ludzik from './images/ludzik.png';
import NavigateSVG from './helpers/NavigateSVG';
import labService from './labService';
import authService from '../auth/authService';
import HashLoader from "react-spinners/HashLoader";

// Define the categories
const allCategories = ["human toxicity", "environmental", "physicochemical properties"];

const LabContent = () => {
  const [expandedRows, setExpandedRows] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]); // Changed from selectedEndpoints
  const [analLoading, setAnalLoading] = useState(true);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [deleteID, setDeleteID] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'start_time', direction: 'descending' });


  const handleMenuItemClick = (path) => {
    navigate(path);
  };



  const handleResultsClick = (id) => {
    navigate(`/laboratory/results/${id}`);
  };

  const handleRunningPred = (id) => {
    navigate(`/laboratory/computation/${id}`);
  };

  const openModal = (id) => {
    setDeleteID(id)
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsConfirmed(false); // Reset confirmation checkbox
  };

  const handleDelete = () => {
    if (currentUser && deleteID && isConfirmed) {
      labService.deleteAnalysis(deleteID, currentUser.access)
        .then(() => {
          navigate('/laboratory/');
        })
        .catch(error => console.error(error))
        .finally(() => closeModal());
    }
  };


  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const fetchPredictions = () => {
      labService.getAnalyses(currentUser.access)
        .then(response => {
          setPredictions(response.data);
          setAnalLoading(false);
        })
        .catch(error => {
          console.error("Błąd podczas pobierania predykcji:", error);
          setAnalLoading(false);
        });
    };

    fetchPredictions();

    const intervalId = setInterval(() => {
      fetchPredictions();
    }, 10000); // 10000 ms = 10 sekund

    return () => {
      clearInterval(intervalId);
    };
  }, [currentUser]);

  const sortedPredictions = [...predictions].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  // Get all unique categories present in the user's analyses
  const userCategories = [...new Set(sortedPredictions.flatMap(anal => anal.selected_categories || []))];

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicatorClass = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? 'ascending' : 'descending';
    }
    return '';
  };

  const setNewPredOpos = () => {
    navigate('/home');
  }

  // Removed the useEffect that was fetching endpoints

  // New function to toggle category filters
  const toggleCategoryFilter = (category) => {
    setSelectedCategories((prevSelected) => {
      if (prevSelected.includes(category)) {
        return prevSelected.filter((c) => c !== category);
      } else {
        return [...prevSelected, category];
      }
    });
  };

  // Updated filter logic
  const filteredPredictions = selectedCategories.length > 0
    ? sortedPredictions.filter((anal) =>
      anal.selected_categories && anal.selected_categories.some(category => selectedCategories.includes(category))
    )
    : sortedPredictions;

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Helper function to capitalize filter buttons
  const capitalize = (s) => s.split(' ').map(word => word.charAt(0).toUpperCase() + word.substring(1)).join(' ');


  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}.${month}.${year}`;
  };

  const handleRowClick = (anal) => {
    const status = anal.anal_status.toLowerCase();
    if (status === 'failed') {
      return;
    } else if (status === 'running') {
      navigate(`/calculating-prediction/${anal.id}`);

    } else {
      navigate(`/summary/${anal.id}`);
    }
  };


  return (
    <main className='main-wrapper'>
      <div className='lab-content'>
        <header className='laboratory-header'>
          <h2>Predictions overview</h2>
          <button type="button" className="new-prediction-button" onClick={() => handleMenuItemClick('/choose-substance')}>New prediction</button>
        </header>

        <div className='manage-models-list'>
          {analLoading ? (
            <div className='loader-div' style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
              <HashLoader className='loader' size={80} color='#0b5578' />
            </div>
          ) : filteredPredictions.length === 0 ? (
            <div className='empty-state-message' style={{ textAlign: 'center', marginTop: '80px', marginBottom: '80px' }}>
              <p className='no-preds' style={{ fontSize: '18px', color: '#555', marginBottom: '8px' }}>No predictions to display.</p>
              <p className='no-preds' style={{ fontSize: '16px', color: '#777' }}>
                Start a new analysis by clicking the <span className='bold' style={{ fontWeight: 'bold' }}>New prediction</span> button
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="license-table">
                <thead>
                  <tr>
                    <th>PREDICTIONS</th>
                    <th>DATE</th>
                    <th>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPredictions.map((anal) => (
                    <tr key={anal.id} onClick={() => handleRowClick(anal)}>
                      <td>
                        <p>{anal.name}</p>
                      </td>
                      <td>{formatDate(anal.start_time)}</td>
                      <td>
                        <span className={`badge ${anal.anal_status.toLowerCase().replace(/\s+/g, '-')}`}>
                          {anal.anal_status.charAt(0).toUpperCase() + anal.anal_status.slice(1).toLowerCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </main>
  );
}
export default LabContent;
